import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FiAlertCircle, FiCheckCircle, FiInfo, FiX } from 'react-icons/fi';
import { HiSparkles } from 'react-icons/hi2';
import { t, useI18n } from './utils/i18n';

const FeedbackContext = createContext({
  showFeedback: () => {},
  closeFeedback: () => {},
});

const MODULE_CLASS = {
  grimoire: 'feedback-dialog--grimoire',
  sigil: 'feedback-dialog--sigil',
  app: 'feedback-dialog--app',
};

const TONE_ICON = {
  error: FiAlertCircle,
  warning: FiAlertCircle,
  success: FiCheckCircle,
  info: FiInfo,
};

const getDefaultTitleKey = (module, tone) => {
  const toneTitle = {
    warning: 'warning_title',
    success: 'success_title',
    info: 'info_title',
    error: 'error_title',
  }[tone] || 'error_title';

  if (module === 'grimoire') {
    return `feedback.grimoire.${toneTitle}`;
  }
  if (module === 'sigil') {
    return `feedback.sigil.${toneTitle}`;
  }
  return `feedback.app.${toneTitle}`;
};

function FeedbackDialog({ feedback, onClose, language }) {
  if (!feedback) return null;
  const module = feedback.module || 'app';
  const tone = feedback.tone || 'error';
  const Icon = TONE_ICON[tone] || FiAlertCircle;
  const moduleClass = MODULE_CLASS[module] || MODULE_CLASS.app;
  const title = feedback.title || t(getDefaultTitleKey(module, tone), language);
  const message = feedback.message || t('feedback.app.fallback_message', language);
  const confirmLabel = feedback.confirmLabel || t('feedback.close', language);

  return (
    <div className="feedback-dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className={`feedback-dialog ${moduleClass} feedback-dialog--${tone}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="feedback-dialog-title"
        aria-describedby="feedback-dialog-message"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="feedback-dialog-close" onClick={onClose} aria-label={confirmLabel}>
          <FiX size={18} />
        </button>
        <div className="feedback-dialog-glow" aria-hidden="true" />
        <div className="feedback-dialog-icon" aria-hidden="true">
          {module === 'grimoire' ? <HiSparkles size={24} /> : <Icon size={24} />}
        </div>
        <div className="feedback-dialog-copy">
          <div className="feedback-dialog-kicker">{t(`feedback.module.${module}`, language)}</div>
          <h2 id="feedback-dialog-title">{title}</h2>
          <p id="feedback-dialog-message">{message}</p>
        </div>
        <button type="button" className="feedback-dialog-action" onClick={onClose}>
          {confirmLabel}
        </button>
      </div>
    </div>
  );
}

export function FeedbackProvider({ children }) {
  const { language } = useI18n();
  const [feedback, setFeedback] = useState(null);

  const closeFeedback = useCallback(() => setFeedback(null), []);
  const showFeedback = useCallback((nextFeedback = {}) => {
    const message = String(nextFeedback.message || '').trim();
    setFeedback({
      module: nextFeedback.module || 'app',
      tone: nextFeedback.tone || 'error',
      title: nextFeedback.title || '',
      message,
      confirmLabel: nextFeedback.confirmLabel || '',
    });
  }, []);

  const contextValue = useMemo(() => ({ showFeedback, closeFeedback }), [showFeedback, closeFeedback]);

  return (
    <FeedbackContext.Provider value={contextValue}>
      {children}
      <FeedbackDialog feedback={feedback} onClose={closeFeedback} language={language} />
    </FeedbackContext.Provider>
  );
}

export const useFeedback = () => useContext(FeedbackContext);
